import { useState, useEffect } from "react";
import { useAuth } from "../lib/AuthContext";
import { apiUpload, apiGet, apiDelete } from "../lib/api";
import { COLORS } from "../lib/colors";
import BulkUpload from "./BulkUpload";

export default function StudentUpload({ programmeId }) {
  const { session } = useAuth();
  const token = session?.access_token;
  const [tab, setTab] = useState("list");
  const [file, setFile] = useState(null);
  const [cohortYear, setCohortYear] = useState(new Date().getFullYear());
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [students, setStudents] = useState([]);
  const [listLoading, setListLoading] = useState(false);

  const loadStudents = async () => {
    if (!programmeId || !token) return;
    setListLoading(true);
    try {
      const data = await apiGet(`/students/${programmeId}/list`, token);
      setStudents(data.students || []);
    } catch {
      setStudents([]);
    } finally {
      setListLoading(false);
    }
  };

  useEffect(() => { loadStudents(); }, [programmeId, token]);

  const handleDelete = async (id, name) => {
    if (!confirm(`Delete ${name}?`)) return;
    try {
      await apiDelete(`/student/cv/${id}`, token);
      setStudents((prev) => prev.filter((s) => s.id !== id));
    } catch (err) {
      alert("Delete failed: " + err.message);
    }
  };

  const handleSingle = async () => {
    if (!file || !programmeId) return;
    setLoading(true);
    setResult(null);
    const formData = new FormData();
    formData.append("file", file);
    try {
      const data = await apiUpload(`/upload/student/${programmeId}?cohort_year=${cohortYear}`, formData, token);
      setResult(data);
      loadStudents();
    } catch (err) {
      setResult({ error: err.message });
    } finally {
      setLoading(false);
    }
  };

  if (!programmeId) {
    return (
      <div style={{
        backgroundColor: COLORS.surface, borderRadius: 12,
        border: `1px solid ${COLORS.border}`, padding: 40, textAlign: "center",
      }}>
        <p style={{ fontSize: 14, color: COLORS.textMuted }}>Select a programme in Setup first.</p>
      </div>
    );
  }

  const tabs = [
    { key: "list", label: `CVs (${students.length})` },
    { key: "bulk", label: "Bulk Upload" },
    { key: "single", label: "Single Upload" },
  ];

  return (
    <div>
      <h2 style={{ fontSize: 20, fontWeight: 700, color: COLORS.text, marginBottom: 4 }}>Student CVs</h2>
      <p style={{ fontSize: 14, color: COLORS.textMuted, marginBottom: 24 }}>
        Upload and manage student CVs. Analyzes diversity, prior education, work experience, and language skills.
      </p>

      <TabBar tabs={tabs} tab={tab} setTab={setTab} />

      {tab === "list" && (
        <StudentTable students={students} loading={listLoading} onDelete={handleDelete} onRefresh={loadStudents} />
      )}

      {tab === "bulk" && (
        <div>
          <CohortYearInput value={cohortYear} onChange={setCohortYear} />
          <BulkUpload uploadPath="student" entityId={programmeId} extraParams={{ cohort_year: cohortYear }} onComplete={loadStudents} />
        </div>
      )}

      {tab === "single" && (
        <div style={{
          backgroundColor: COLORS.surface, borderRadius: 12,
          border: `1px solid ${COLORS.border}`, padding: 24,
        }}>
          <CohortYearInput value={cohortYear} onChange={setCohortYear} />
          <input type="file" accept=".pdf,.docx,.txt" onChange={(e) => setFile(e.target.files[0])} style={{ marginBottom: 16 }} />
          <br />
          <button
            onClick={handleSingle}
            disabled={!file || loading}
            style={{
              padding: "10px 24px", fontSize: 14, fontWeight: 600,
              backgroundColor: file && !loading ? COLORS.accent : COLORS.border,
              color: file && !loading ? "#fff" : COLORS.textMuted,
              border: "none", borderRadius: 8, cursor: file && !loading ? "pointer" : "not-allowed",
            }}
          >{loading ? "Processing..." : "Process CV"}</button>

          {result && !result.error && (
            <div style={{ marginTop: 20, padding: 16, borderRadius: 12, border: `1px solid ${COLORS.border}`, backgroundColor: COLORS.surfaceAlt }}>
              <div style={{ fontSize: 12, color: COLORS.textMuted }}>Score</div>
              <div style={{ fontSize: 28, fontWeight: 700, color: COLORS.accent }}>{result.efmd_score}/100</div>
              {result.nationality && <div style={{ fontSize: 13, color: COLORS.textSecondary, marginTop: 4 }}>Nationality: {result.nationality}</div>}
            </div>
          )}
          {result?.error && (
            <div style={{ marginTop: 16, backgroundColor: COLORS.redLight, color: COLORS.red, padding: "8px 12px", borderRadius: 8, fontSize: 13 }}>
              {result.error}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function StudentTable({ students, loading, onDelete, onRefresh }) {
  if (loading) {
    return <div style={{ padding: 40, textAlign: "center", color: COLORS.textMuted, fontSize: 14 }}>Loading student CVs...</div>;
  }

  if (students.length === 0) {
    return (
      <div style={{
        backgroundColor: COLORS.surface, borderRadius: 12,
        border: `1px solid ${COLORS.border}`, padding: 40, textAlign: "center",
      }}>
        <p style={{ fontSize: 14, color: COLORS.textMuted }}>No student CVs uploaded yet. Use Bulk Upload or Single Upload to add CVs.</p>
      </div>
    );
  }

  const recommended = students.filter((s) => s.include_recommended);
  const avgScore = Math.round(students.reduce((sum, s) => sum + (s.efmd_score || 0), 0) / students.length);
  const nationalities = [...new Set(students.map((s) => s.nationality).filter(Boolean))];

  return (
    <div>
      <div style={{ display: "flex", gap: 16, marginBottom: 16 }}>
        <StatCard label="Total CVs" value={students.length} />
        <StatCard label="Recommended" value={recommended.length} color={COLORS.green} />
        <StatCard label="Avg Score" value={avgScore} />
        <StatCard label="Nationalities" value={nationalities.length} color={COLORS.purple} />
        <div style={{ flex: 1 }} />
        <button onClick={onRefresh} style={{
          padding: "6px 14px", fontSize: 12, border: `1px solid ${COLORS.border}`,
          borderRadius: 6, backgroundColor: COLORS.surface, color: COLORS.textSecondary, cursor: "pointer",
        }}>Refresh</button>
      </div>

      <div style={{
        backgroundColor: COLORS.surface, borderRadius: 12,
        border: `1px solid ${COLORS.border}`, overflow: "hidden",
      }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
          <thead>
            <tr style={{ backgroundColor: COLORS.surfaceAlt, borderBottom: `1px solid ${COLORS.border}` }}>
              <th style={thStyle}>Name</th>
              <th style={thStyle}>Nationality</th>
              <th style={thStyle}>Gender</th>
              <th style={thStyle}>Prior Degree</th>
              <th style={thStyle}>Work Exp</th>
              <th style={thStyle}>Cohort</th>
              <th style={{ ...thStyle, textAlign: "center" }}>Score</th>
              <th style={{ ...thStyle, textAlign: "center" }}>Include</th>
              <th style={{ ...thStyle, width: 40 }}></th>
            </tr>
          </thead>
          <tbody>
            {students.map((s) => (
              <StudentRow key={s.id} s={s} onDelete={onDelete} />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function CvDownloadBtn({ cvType, cvId, name }) {
  const { session } = useAuth();
  const handleDownload = async (e) => {
    e.stopPropagation();
    const API_URL = import.meta.env.VITE_API_URL || "https://api-calibre.edtechsolutions.io";
    try {
      const res = await fetch(`${API_URL}/cv/${cvType}/${cvId}/download`, {
        headers: { Authorization: `Bearer ${session?.access_token}` },
      });
      if (!res.ok) throw new Error("Download failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${name.replace(/\s+/g, '_')}_CV.txt`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) { alert("CV download failed: " + err.message); }
  };
  return (
    <button onClick={handleDownload} style={{
      padding: "4px 12px", fontSize: 11, border: `1px solid #58a6ff`,
      borderRadius: 4, background: "none", color: '#58a6ff', cursor: "pointer",
    }}>↓ CV</button>
  );
}

function StudentRow({ s, onDelete }) {
  const [expanded, setExpanded] = useState(false);
  const workYrs = s.work_experience_years || 0;
  const langs = s.languages?.length || 0;
  const isIntl = s.nationality && s.nationality !== "Finnish";

  const criteria = [
    { code: "3.1.2", name: "Work Experience", value: workYrs > 0 ? `${workYrs} years` : "None", ok: workYrs >= 3 },
    { code: "3.1.2", name: "Qualifications", value: s.prior_degree ? `${s.prior_degree}${s.prior_degree_field ? " " + s.prior_degree_field : ""}` : "—", ok: !!s.prior_degree },
    { code: "3.1.3", name: "International", value: s.nationality || "—", ok: isIntl },
    { code: "3.1.2", name: "Gender", value: s.gender || "—", ok: null },
    { code: "3.1.2", name: "Languages", value: langs > 0 ? `${langs} languages` : "No data", ok: langs >= 2 },
  ];

  return (
    <>
      <tr onClick={() => setExpanded(!expanded)} style={{ borderBottom: `1px solid ${COLORS.borderLight}`, cursor: "pointer" }}>
        <td style={tdStyle}><span style={{ fontWeight: 500 }}>{s.full_name}</span></td>
        <td style={tdStyle}>{s.nationality || "—"}</td>
        <td style={tdStyle}>{s.gender || "—"}</td>
        <td style={tdStyle}>{s.prior_degree || "—"}</td>
        <td style={tdStyle}>{workYrs > 0 ? `${workYrs}y` : "—"}</td>
        <td style={tdStyle}>{s.cohort_year || "—"}</td>
        <td style={{ ...tdStyle, textAlign: "center" }}><ScoreBadge score={s.efmd_score} /></td>
        <td style={{ ...tdStyle, textAlign: "center" }}>
          {s.include_recommended ? <span style={{ color: COLORS.green, fontWeight: 600 }}>Yes</span> : <span style={{ color: COLORS.orange }}>Review</span>}
        </td>
        <td style={tdStyle}><span style={{ color: COLORS.textMuted, fontSize: 10 }}>{expanded ? "▲" : "▼"}</span></td>
      </tr>
      {expanded && (
        <tr>
          <td colSpan={9} style={{ padding: 0 }}>
            <div style={{ background: COLORS.surfaceAlt, padding: "8px 16px", borderBottom: `1px solid ${COLORS.border}` }}>
              {criteria.map((c, i) => (
                <div key={i} style={{
                  display: "flex", alignItems: "center", padding: "6px 8px",
                  borderBottom: i < criteria.length - 1 ? `1px solid ${COLORS.borderLight}` : "none", fontSize: 13,
                }}>
                  <span style={{
                    width: 20, height: 20, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center",
                    fontSize: 10, fontWeight: 700, flexShrink: 0,
                    background: c.ok === true ? COLORS.green : c.ok === false ? COLORS.orange : COLORS.border,
                    color: c.ok != null ? "#fff" : COLORS.textMuted,
                  }}>{c.ok === true ? "✓" : c.ok === false ? "!" : "—"}</span>
                  <span style={{ width: 50, color: COLORS.textMuted, fontFamily: "monospace", fontSize: 11, marginLeft: 8 }}>{c.code}</span>
                  <span style={{ width: 120, color: COLORS.textSecondary }}>{c.name}</span>
                  <span style={{ fontWeight: 600, color: COLORS.text }}>{c.value}</span>
                </div>
              ))}
              <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, paddingTop: 6 }}>
                <CvDownloadBtn cvType="student" cvId={s.id} name={s.full_name} />
                <button onClick={(e) => { e.stopPropagation(); onDelete(s.id, s.full_name); }}
                  style={{ padding: "4px 12px", fontSize: 11, border: `1px solid ${COLORS.red}`, borderRadius: 4, background: "none", color: COLORS.red, cursor: "pointer" }}>Delete</button>
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

function CohortYearInput({ value, onChange }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <label style={{ fontSize: 13, fontWeight: 500, color: COLORS.textSecondary, marginRight: 8 }}>Cohort Year:</label>
      <input
        type="number" min={2020} max={2030} value={value}
        onChange={(e) => onChange(parseInt(e.target.value))}
        style={{ padding: "6px 10px", fontSize: 14, border: `1px solid ${COLORS.border}`, borderRadius: 6, width: 80, backgroundColor: COLORS.surfaceAlt, color: COLORS.text }}
      />
    </div>
  );
}

function TabBar({ tabs, tab, setTab }) {
  return (
    <div style={{ display: "flex", gap: 8, marginBottom: 20 }}>
      {tabs.map((t) => (
        <button
          key={t.key}
          onClick={() => setTab(t.key)}
          style={{
            padding: "8px 20px", fontSize: 13, fontWeight: 500, borderRadius: 8,
            border: `1px solid ${tab === t.key ? COLORS.accent : COLORS.border}`,
            backgroundColor: tab === t.key ? COLORS.accentLight : COLORS.surface,
            color: tab === t.key ? COLORS.accent : COLORS.textSecondary,
            cursor: "pointer",
          }}
        >{t.label}</button>
      ))}
    </div>
  );
}

function StatCard({ label, value, color }) {
  return (
    <div style={{
      backgroundColor: COLORS.surface, border: `1px solid ${COLORS.border}`,
      borderRadius: 8, padding: "10px 16px", minWidth: 80,
    }}>
      <div style={{ fontSize: 11, color: COLORS.textMuted, marginBottom: 2 }}>{label}</div>
      <div style={{ fontSize: 20, fontWeight: 700, color: color || COLORS.text }}>{value}</div>
    </div>
  );
}

function ScoreBadge({ score }) {
  const color = score >= 65 ? COLORS.green : score >= 45 ? COLORS.orange : COLORS.red;
  const bg = score >= 65 ? COLORS.greenLight : score >= 45 ? COLORS.orangeLight : COLORS.redLight;
  return (
    <span style={{
      display: "inline-block", padding: "2px 8px", borderRadius: 4,
      fontSize: 12, fontWeight: 600, color, backgroundColor: bg,
    }}>{score}</span>
  );
}

const thStyle = {
  textAlign: "left", padding: "10px 12px", fontSize: 11, fontWeight: 600,
  color: COLORS.textMuted, textTransform: "uppercase", letterSpacing: "0.5px",
};

const tdStyle = {
  padding: "10px 12px", color: COLORS.text,
};
